import { ReactElement, useState, useRef } from 'react'

import noop from 'lodash/noop'
import Draggable, { DraggableData } from 'react-draggable'

import { Modal, type ModalProps } from './Modal'

export interface DraggableModalProps extends ModalProps {
    children: ReactElement
}

export const DraggableModal = ({
    open,
    onCancel,
    title,
    footer,
    children,
    ...props
}: DraggableModalProps): ReactElement => {
    const [disabled, setDisabled] = useState(true)
    const [bounds, setBounds] = useState({
        left: 0,
        top: 0,
        bottom: 0,
        right: 0,
    })

    const draggableRef = useRef<HTMLInputElement>(null)

    const onStart = (uiData: DraggableData): void => {
        const { clientWidth, clientHeight } =
            window?.document?.documentElement ?? {}
        const targetRect = draggableRef?.current?.getBoundingClientRect()

        if (!targetRect) {
            return
        }

        const x = uiData?.x ?? 0
        const y = uiData?.y ?? 0
        setBounds({
            left: -targetRect.left + x,
            right: clientWidth - (targetRect.right - x),
            top: -targetRect.top + y,
            bottom: clientHeight - (targetRect.bottom - y),
        })
    }

    return (
        <Modal
            open={open}
            title={
                <div
                    style={{
                        width: '100%',
                        cursor: 'move',
                    }}
                    onMouseOver={() => {
                        if (disabled) {
                            setDisabled(false)
                        }
                    }}
                    onMouseOut={() => setDisabled(true)}
                    onFocus={() => noop}
                    onBlur={() => noop}
                >
                    {title}
                </div>
            }
            onCancel={onCancel}
            modalRender={(modal) => (
                <Draggable
                    disabled={disabled}
                    bounds={bounds}
                    nodeRef={draggableRef}
                    onStart={(_, uiData) => onStart(uiData)}
                >
                    <div ref={draggableRef}>{modal}</div>
                </Draggable>
            )}
            destroyOnHidden
            footer={footer}
            {...props}
        >
            {children}
        </Modal>
    )
}

DraggableModal.displayName = 'DraggableModal'
